import React, { useEffect, useRef } from 'react';

/**
 * AmbientGlow — a lightweight canvas layer of warm, out-of-focus
 * "bar light" bokeh drifting slowly upward. Gives the hero a living,
 * golden-hour feel with zero dependencies and near-zero cost:
 *  - respects prefers-reduced-motion (renders nothing)
 *  - pauses when the tab is hidden
 *  - devicePixelRatio aware, capped at 2x
 * Purely decorative: aria-hidden, pointer-events none.
 */
const AmbientGlow = ({ count = 18, className = '' }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf = 0;
    let running = true;
    let width = 0;
    let height = 0;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    // Warm brass/amber palette pulled from the site's tokens
    const HUES = [
      [201, 162, 39],   // #C9A227 brass
      [212, 165, 116],  // #D4A574 tan
      [255, 214, 130],  // candlelight
    ];

    const rand = (a, b) => a + Math.random() * (b - a);
    const orbs = Array.from({ length: count }, () => {
      const [r, g, b] = HUES[Math.floor(Math.random() * HUES.length)];
      return {
        x: Math.random(),               // 0..1 of width
        y: Math.random(),               // 0..1 of height
        radius: rand(14, 66),           // soft big bokeh discs
        speed: rand(0.008, 0.028),      // fraction of height per second
        drift: rand(-0.006, 0.006),     // sideways sway
        phase: rand(0, Math.PI * 2),    // twinkle offset
        twinkle: rand(0.4, 1.1),        // twinkle speed
        alpha: rand(0.05, 0.16),        // base opacity — keep it subtle
        r, g, b,
      };
    });

    let last = performance.now();
    const frame = (now) => {
      if (!running) return;
      const dt = Math.min((now - last) / 1000, 0.1);
      last = now;

      ctx.clearRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'lighter';

      for (const o of orbs) {
        o.y -= o.speed * dt;
        o.x += o.drift * dt;
        o.phase += o.twinkle * dt;
        // recycle at the top
        if (o.y * height < -o.radius * 2) {
          o.y = 1 + (o.radius * 2) / Math.max(height, 1);
          o.x = Math.random();
        }
        const cx = o.x * width;
        const cy = o.y * height;
        const a = o.alpha * (0.65 + 0.35 * Math.sin(o.phase));
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, o.radius);
        grad.addColorStop(0, `rgba(${o.r}, ${o.g}, ${o.b}, ${a})`);
        grad.addColorStop(0.6, `rgba(${o.r}, ${o.g}, ${o.b}, ${a * 0.35})`);
        grad.addColorStop(1, `rgba(${o.r}, ${o.g}, ${o.b}, 0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(cx, cy, o.radius, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    const onVisibility = () => {
      running = document.visibilityState === 'visible';
      if (running) {
        last = performance.now();
        raf = requestAnimationFrame(frame);
      } else {
        cancelAnimationFrame(raf);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('resize', resize);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('resize', resize);
    };
  }, [count]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
    />
  );
};

export default AmbientGlow;
